import { useQueries } from "@tanstack/react-query";
import { Check, Sparkles } from "lucide-react";

import { getBusinessTier, getTiers } from "src/api/settings";
import { useAuth } from "src/providers/AuthProvider";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import CustomModal from "src/components/CustomModal";
import UpgradeToTier2 from "src/components/modals/UpgradeToTier2";
import { Button } from "src/components/ui/button";
import { SettingsPanel } from "src/components/common";
import { cn, renderCurrency } from "src/lib/utils";
import type { AccountTier as Tier } from "src/types/settings";

const AccountTier = () => {
	const { user } = useAuth();

	const [{ data: rawTiers }, { data: rawBusinessTier }] = useQueries({
		queries: [
			{ queryKey: ["account-tiers"], queryFn: () => getTiers(), suspense: true },
			{ queryKey: ["account-tiers", "business"], queryFn: () => getBusinessTier(), suspense: true },
		],
	});

	const tiers = rawTiers?.data ?? [];
	const businessTier = rawBusinessTier?.data;
	const currentTier = businessTier?.tier ?? 0;

	return (
		<SettingsPanel title="Account Tier" description="The higher your tier, the higher your transaction limits.">
			<div className="tw-space-y-4">
				{businessTier && <TierCard tier={businessTier} active />}

				{tiers.filter((tier) => (tier.tier ?? 0) > currentTier).length > 0 && (
					<div>
						<p className="tw-mb-2.5 tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-text-soft">
							Available upgrades
						</p>
						<div className="tw-space-y-3">
							{tiers
								.filter((tier) => (tier.tier ?? 0) > currentTier)
								.map((tier) => (
									<TierCard tier={tier} key={tier.tier} />
								))}
						</div>
					</div>
				)}

				{!user?.bvn_verified && (
					<CustomModal
						title="Upgrade to Tier 2"
						trigger={
							<Button className="tw-w-full tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
								<Sparkles className="tw-h-4 tw-w-4" />
								Upgrade to Tier 2
							</Button>
						}
					>
						<UpgradeToTier2 />
					</CustomModal>
				)}
			</div>
		</SettingsPanel>
	);
};

const TierCard = ({ tier, active }: { tier: Tier; active?: boolean }) => (
	<div
		className={cn(
			"tw-rounded-lg tw-border tw-p-4 sm:tw-p-5",
			active ? "tw-border-navy tw-bg-navy tw-text-white" : "tw-border-border tw-bg-white"
		)}
	>
		<div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-gap-3">
			<p className={cn("tw-text-[18px] tw-font-bold", active ? "tw-text-white" : "tw-text-text")}>Tier {tier.tier}</p>
			{active && (
				<span className="tw-rounded-full tw-bg-teal tw-px-2.5 tw-py-1 tw-text-[11.5px] tw-font-bold tw-text-white">Current</span>
			)}
		</div>

		<div className="tw-mb-3 tw-grid tw-gap-3 sm:tw-grid-cols-2">
			<div>
				<p className={cn("tw-text-[12.5px]", active ? "tw-text-white/70" : "tw-text-text-soft")}>Daily Transaction Limit</p>
				<p className={cn("tw-text-[18px] tw-font-bold", active ? "tw-text-white" : "tw-text-text")}>
					{renderCurrency(tier.daily_limit ?? 0)}
				</p>
			</div>
			<div>
				<p className={cn("tw-text-[12.5px]", active ? "tw-text-white/70" : "tw-text-text-soft")}>Maximum Transaction Limit</p>
				<p className={cn("tw-text-[18px] tw-font-bold", active ? "tw-text-white" : "tw-text-text")}>
					{renderCurrency(tier.max_limit ?? 0)}
				</p>
			</div>
		</div>

		{tier.requirements && tier.requirements.length > 0 && (
			<>
				<p className={cn("tw-mb-1.5 tw-text-[12.5px]", active ? "tw-text-white/70" : "tw-text-text-soft")}>Requirements</p>
				<ul className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1">
					{tier.requirements.map((requirement, index) => (
						<li
							key={index}
							className={cn("tw-flex tw-items-center tw-gap-1.5 tw-text-[13px]", active ? "tw-text-white" : "tw-text-text")}
						>
							<Check className={cn("tw-h-3.5 tw-w-3.5", active ? "tw-text-teal" : "tw-text-teal")} />
							{requirement}
						</li>
					))}
				</ul>
			</>
		)}
	</div>
);

export default withPermissionWrapper(AccountTier, "view_business_tier");
